import React, { useState } from 'react'
import { Message } from 'rsuite'
import useStandardToaster from '../../ui_reusable/StandardToaster'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import UniversalLoader from '../../ui_reusable/UniversalLoader'
import Modal from '../../ui_reusable/Modal'
import { StandardButton } from '../../ui_reusable/styles/ButtonStyles'
import {
  StandardInput,
  StandardDescription,
} from '../../ui_reusable/StandardInputs'
import theme from '../../../styles/theme'
import FAQAccordion from '../ui_reusable/FAQAccordion'
import {
  useLearningsRetrieve,
  useLearningsPartialUpdate,
} from '../../../api/generated/endpoints/learning/learning'
import type { PatchedLearningRequest } from '../../../api/generated/models'
import {
  learningsPartialUpdateBody,
  learningsRetrieveResponse,
} from '../../../api/generated/validation/learning/learning.zod'
// Import our enhanced validation utility
import { validateAndLog, validateForRequest } from '../../../utils/zodDebugger'

import {
  FAQContainer,
  FormGroup,
  MainButtonContainer,
  FormLabel,
  ModalText,
  ModalHighlight,
} from '../ui_reusable/styles/FAQ.styles'

interface EditableFaqItem {
  id: number
  question: string
  answer: string
}

const ManageFAQPage: React.FC = () => {
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false)
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [newFaq, setNewFaq] = useState<EditableFaqItem>({
    id: 0,
    question: '',
    answer: '',
  })
  const [editedFaq, setEditedFaq] = useState<EditableFaqItem>({
    id: 0,
    question: '',
    answer: '',
  })
  const [selectedFaq, setSelectedFaq] = useState<EditableFaqItem | null>(null)
  const { pushToast } = useStandardToaster()

  // Use the orval-generated query hook with no caching for real-time updates
  const {
    data: learningData,
    isLoading,
    error,
    refetch,
  } = useLearningsRetrieve({
    query: {
      staleTime: 0,
      gcTime: 0,
    },
  })

  // Runtime validation with enhanced debugging (one-liner)
  const validatedData = validateAndLog(
    learningsRetrieveResponse,
    learningData,
    'ManageFAQPage',
  )

  // Get FAQ data from validated response - note: FAQ structure may need adjustment based on actual API response
  const faqData: any[] = Array.isArray(validatedData?.data?.faq)
    ? validatedData!.data!.faq
    : []

  // Use the orval-generated mutation hook
  const updateLearningMutation = useLearningsPartialUpdate({
    mutation: {
      onSuccess: () => {
        // Simply refetch the data after successful mutation
        refetch()

        pushToast(<Message type="success">FAQ updated successfully.</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      },
      onError: () => {
        pushToast(<Message type="error">Failed to update FAQ.</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      },
    },
  })

  const handleEditClick = (id: number): void => {
    const faqToEdit = faqData.find((e) => e.id === id)
    if (faqToEdit) {
      setEditedFaq({
        id: faqToEdit.id || 0,
        question: faqToEdit.question || '',
        answer: faqToEdit.answer || '',
      })
      setEditModalOpen(true)
    }
  }

  const handleAddClick = (): void => {
    const maxId =
      faqData.length > 0 ? Math.max(...faqData.map((f) => f.id || 0)) : 0
    setNewFaq({ id: maxId + 1, question: '', answer: '' })
    setAddModalOpen(true)
  }

  const handleChange = (
    value: any,
    field: keyof EditableFaqItem,
    isEdit: boolean = false,
  ): void => {
    // Extract the value from the event object if it exists
    const actualValue = value?.target?.value ?? value

    if (isEdit) {
      setEditedFaq((prev) => ({ ...prev, [field]: actualValue }))
    } else {
      setNewFaq((prev) => ({ ...prev, [field]: actualValue }))
    }
  }

  const updateFAQData = (updatedFaqArray: any[]): void => {
    const updateData: PatchedLearningRequest = {
      faq: updatedFaqArray,
    }

    // Validate data with enhanced debugging (for development/debugging only)
    try {
      const validData = validateForRequest(
        learningsPartialUpdateBody,
        updateData,
        'UpdateFAQ',
      )
      updateLearningMutation.mutate({ data: validData })
    } catch (error) {
      // Error already logged to console with detailed debugging
      pushToast(
        <Message type="error">
          Please check the FAQ data and try again.
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleSaveEdit = (id: number): void => {
    if (!editedFaq.question.trim() || !editedFaq.answer.trim()) {
      pushToast(
        <Message type="warning">Question and answer cannot be empty.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const updatedData: any[] = faqData.map((item) =>
      item.id === id
        ? {
            id: editedFaq.id,
            question: editedFaq.question,
            answer: editedFaq.answer,
          }
        : {
            id: item.id || 0,
            question: item.question || '',
            answer: item.answer || '',
          },
    )

    updateFAQData(updatedData)
    setEditModalOpen(false)
  }

  const handleSaveNew = (): void => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      pushToast(
        <Message type="warning">Question and answer cannot be empty.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const updatedData: any[] = [
      ...faqData.map((item) => ({
        id: item.id || 0,
        question: item.question || '',
        answer: item.answer || '',
      })),
      { id: newFaq.id, question: newFaq.question, answer: newFaq.answer },
    ]

    updateFAQData(updatedData)
    setAddModalOpen(false)
  }

  const handleDelete = (id: number): void => {
    const updatedData: any[] = faqData
      .filter((e) => e.id !== id)
      .map((item) => ({
        id: item.id || 0,
        question: item.question || '',
        answer: item.answer || '',
      }))

    updateFAQData(updatedData)
  }

  const handleDeleteClick = (id: number): void => {
    const faqToDelete = faqData.find((e) => e.id === id)
    if (faqToDelete) {
      setSelectedFaq({
        id: faqToDelete.id || 0,
        question: faqToDelete.question || '',
        answer: faqToDelete.answer || '',
      })
      setDeleteModalOpen(true)
    }
  }

  const handleConfirmDelete = (): void => {
    if (!selectedFaq) return

    handleDelete(selectedFaq.id)
    setDeleteModalOpen(false)
    setSelectedFaq(null)
  }

  if (error) {
    return <div>Error loading FAQ content</div>
  }

  return (
    <>
      <SimpleAccordion id={'manageFAQPageAccordion'} defaultActiveKey={['1']}>
        <SimpleAccordion.Panel header="Manage FAQ" eventKey="1" noCollapse>
          <FAQContainer>
            <UniversalLoader
              show={isLoading}
              content="Loading FAQ content..."
            />
            <MainButtonContainer>
              <StandardButton
                onClick={handleAddClick}
                style={{ marginBottom: '20px', width: '10%' }}
                disabled={updateLearningMutation.isPending}
              >
                Add FAQ
              </StandardButton>
            </MainButtonContainer>
            <FAQAccordion id={'manageFAQAccordion'}>
              {faqData.map((elem, i) => (
                <FAQAccordion.Panel
                  key={elem.id || i}
                  eventKey={`faq-${elem.id}`}
                  header={elem.question || ''}
                  text={elem.answer || ''}
                  onEdit={() => handleEditClick(elem.id || 0)}
                  onDelete={() => handleDeleteClick(elem.id || 0)}
                />
              ))}
            </FAQAccordion>
          </FAQContainer>

          {/* Edit Modal */}
          <Modal
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            title="Edit FAQ"
            size={'lg'}
            confirmText="Save"
            cancelText="Cancel"
            onConfirm={() => handleSaveEdit(editedFaq.id)}
            onCancel={() => setEditModalOpen(false)}
            confirmColor={theme.colors.primary}
          >
            <Modal.Body>
              <FormGroup>
                <FormLabel>Question</FormLabel>
                <StandardInput
                  style={{ width: '100%' }}
                  value={editedFaq.question}
                  onChange={(value: any) =>
                    handleChange(value, 'question', true)
                  }
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Answer</FormLabel>
                <StandardDescription
                  value={editedFaq.answer}
                  onChange={(value: any) => handleChange(value, 'answer', true)}
                  rows={4}
                />
              </FormGroup>
            </Modal.Body>
          </Modal>

          {/* Add Modal */}
          <Modal
            open={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            title="Add FAQ"
            confirmText="Add"
            size={'lg'}
            cancelText="Cancel"
            onConfirm={handleSaveNew}
            onCancel={() => setAddModalOpen(false)}
            confirmColor={theme.colors.primary}
          >
            <Modal.Body>
              <FormGroup>
                <FormLabel>Question</FormLabel>
                <StandardInput
                  style={{ width: '100%' }}
                  value={newFaq.question}
                  onChange={(value: any) => handleChange(value, 'question')}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Answer</FormLabel>
                <StandardDescription
                  value={newFaq.answer}
                  onChange={(value: any) => handleChange(value, 'answer')}
                  rows={4}
                />
              </FormGroup>
            </Modal.Body>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            open={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            title="Delete FAQ"
            size={'md'}
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={handleConfirmDelete}
            onCancel={() => setDeleteModalOpen(false)}
            confirmColor={theme.colors.error}
          >
            <Modal.Body>
              <ModalText>Are you sure you want to delete this FAQ?</ModalText>
              <ModalText>
                <ModalHighlight>Question:</ModalHighlight>{' '}
                {selectedFaq?.question}
              </ModalText>
            </Modal.Body>
          </Modal>
        </SimpleAccordion.Panel>
      </SimpleAccordion>
    </>
  )
}

export default ManageFAQPage
